import { format } from 'date-fns';
import { toast } from 'react-toastify';
import { useCallback, useMemo } from 'react';
import { FormikValues } from 'formik';
import { AppointmentWithCustomerAndEmployee } from '../../interfaces';
import useGetDepositTransferAppointmets from '../../hooks/appointments/useGetDepositTransferAppointmets';
import FormSmallModal, { FormData } from '../global/FormSmallModal';
import {
  appointmentTransferDepositSchema,
  AppointmentTransferDepositState,
  PaymentForDepositTransfer,
  SelectedAppointment,
} from '../../types/appointments';
import { getFullName } from '../../helpers/global';
import { formatDollarAmount } from '../../utils/global/helper';
import useTransferAppointmentDeposit from '../../hooks/appointments/useTransferAppointmentDeposit';
import {
  invalidateAppointmentPayments,
  invalidateDepositTransferAppointments,
} from '../../services/appointments';
import useTransferPaymentDeposit from '../../hooks/appointments/useTransferPaymentDeposit';

interface Props {
  isOpen: boolean;
  onClose: () => void;
  appointment: SelectedAppointment;
  onRefresh: () => void | Promise<void>;
}

const FORMAT = 'MMM d, yyyy h:mm a';

const getAppointmentLabel = (
  appointment: AppointmentWithCustomerAndEmployee,
  omitDeposit = false,
) =>
  `${getFullName(appointment.customer)} • ${
    appointment.startDateTime
      ? format(new Date(appointment.startDateTime!), FORMAT)
      : 'Unscheduled appointment'
  }${
    omitDeposit
      ? ''
      : ` • ${formatDollarAmount(appointment.depositPrice || 0, true)}`
  }`;

const getPaymentLabel = (payment: PaymentForDepositTransfer) =>
  `${getFullName(payment.customer.user)} • ${format(
    new Date(payment.date),
    FORMAT,
  )} • ${formatDollarAmount(payment.depositPrice, true)}`;

const extractDate = (
  obj: { date: string } | AppointmentWithCustomerAndEmployee,
) => new Date(('date' in obj ? obj.date : obj.startDateTime)!);

export default function TransferAppointmentDepositModal({
  isOpen,
  onClose,
  appointment,
  onRefresh,
}: Props) {
  const { data, isFetching, isError } = useGetDepositTransferAppointmets({
    customerId: appointment.customerId,
    enabled: isOpen,
  });

  const refresh = useCallback(async () => {
    await onRefresh();
    await invalidateAppointmentPayments(appointment.appointmentId);
  }, [onRefresh, appointment.appointmentId]);

  const { mutateAsync: transferAppointmentDeposit } =
    useTransferAppointmentDeposit({
      onRefresh: refresh,
      onSuccess: () => {
        onClose();
        invalidateDepositTransferAppointments();
      },
      onError: toast.error,
    });
  const { mutateAsync: transferPaymentDeposit } = useTransferPaymentDeposit({
    onRefresh: refresh,
    onSuccess: () => {
      onClose();
      invalidateDepositTransferAppointments();
    },
    onError: toast.error,
  });

  const sortedFrom = useMemo(
    () =>
      [...(data?.fromPayments ?? []), ...(data?.from ?? [])].sort((a, b) => {
        const dateA = extractDate(a);
        const dateB = extractDate(b);
        return dateB.getTime() - dateA.getTime();
      }),
    [data?.from, data?.fromPayments],
  );

  const form: FormData<AppointmentTransferDepositState> = {
    title: 'Transfer a Deposit',
    initialValues: {
      fromAppointmentId: '',
      toAppointmentId: appointment.appointmentId,
    },
    validationSchema: appointmentTransferDepositSchema,
    onSubmit: async values => {
      if (values.fromAppointmentId.startsWith('payment:')) {
        const paymentId = values.fromAppointmentId.split(':')[1];
        await transferPaymentDeposit({
          ...values,
          customerId: appointment.customerId,
          fromPaymentId: paymentId,
        });
      } else {
        await transferAppointmentDeposit({
          ...values,
          customerId: appointment.customerId,
        });
      }
    },
    inputRows: [
      [
        {
          label: 'Transfer deposit from',
          placeholder: 'Select a payment',
          inputType: 'select',
          name: 'fromAppointmentId',
          options: sortedFrom.map(appt => ({
            label:
              'appointmentId' in appt
                ? getAppointmentLabel(appt)
                : getPaymentLabel(appt),
            value:
              'appointmentId' in appt
                ? appt.appointmentId
                : `payment:${appt.paymentId}`,
          })),
          disabled: isFetching || isError,
        },
      ],
      [
        {
          label: 'Transfer deposit to',
          placeholder: 'Select an appointment',
          inputType: 'select',
          name: 'toAppointmentId',
          options: (data?.to ?? []).map(appt => ({
            label: getAppointmentLabel(appt, true),
            value: appt.appointmentId,
          })),
          disabled: true,
        },
      ],
    ],
    submitButtonText: 'Transfer',
  };

  return (
    <FormSmallModal
      open={isOpen}
      onClose={onClose}
      form={form}
      maxWidth={500}
      alert={(vals: FormikValues) => {
        const values = vals as AppointmentTransferDepositState;
        const isValid =
          isFetching ||
          !values.toAppointmentId ||
          data?.to.some(appt => appt.appointmentId === values.toAppointmentId);
        if (!isValid) {
          return {
            title: 'Invalid Appointment',
            subtitle:
              'The selected "Transfer deposit to" appointment is not available for transfer',
            type: 'error',
            animate: false,
            dismissible: false,
          };
        }
        if (!isFetching && isValid && !data?.from?.length) {
          return {
            subtitle:
              "Here is where you will find deposit payments from the customer's profile that can be transferred to the selected appointment. There are currently no payments available to transfer.",
            type: 'info',
            animate: false,
            dismissible: false,
          };
        }
        return null;
      }}
    />
  );
}
